import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { UserService } from '../dataServices/user';
import { SessionService } from '../other/session';

/**
 * Signup is invitation-only. The /sign-up route is reachable only when an
 * `invitationToken` query parameter is present (employee invites, external
 * attendees, shared-with, etc.), OR when an external-provider login (Microsoft/
 * Google) sends an already-authenticated user here to complete registration
 * (`external=true`, carrying userId + tokens from the backend login response),
 * OR when the email/password sign-in finds an account whose sign-up was never
 * finished (`resume=true` + userId — the password was already checked there).
 * Everyone else is sent back to login.
 *
 * Whoever is still signed in on this browser is signed out first, silently, the
 * same way the login page does it. The person signing up is someone else, and
 * every request the page makes would otherwise carry the previous user's token
 * and enterprise. None of the routes above saves a session for the person
 * signing up before coming here, so nothing of theirs is lost.
 *
 * The company-onboarding admin is NOT affected: that flow renders the sign-up
 * component inside a dialog (isDialogMode), not via this route.
 */
export const signupInviteGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const sessionService = inject(SessionService);
  const userService = inject(UserService);

  const hasInvitation = !!route.queryParamMap.get('invitationToken');
  const isExternalSignup = route.queryParamMap.get('external') === 'true';
  const isResumedSignup =
    route.queryParamMap.get('resume') === 'true' && !!route.queryParamMap.get('userId');
  if (!hasInvitation && !isExternalSignup && !isResumedSignup) {
    return router.createUrlTree(['/login']);
  }

  // Awaited here rather than in the page's ngOnInit: the page starts its invitation lookups the
  // moment it initialises, and they must already go out without the previous user's token.
  if (sessionService.getSessionToken()) {
    await userService.logoutWithCleanup();
  }
  return true;
};
